import { useState, useEffect, useMemo } from 'react'
import { FiX, FiSearch, FiMapPin, FiUser, FiPhone, FiUsers, FiAward, FiLoader, FiStar, FiInbox, FiSend, FiArrowLeft, FiCheck, FiAlertTriangle } from 'react-icons/fi'
import SidePanel from './SidePanel'

interface Leader {
  id: string
  code: string
  name: string
  district_code?: number
  group_name?: string
  phone?: string
  phone_formatted?: string
  recrutas_count?: number
  own_sales?: number
  distance?: number | null
  // [2026-07-27] Distancia calculada mesmo quando a coordenada da lider e fraca
  // (centroide de cidade). Nao e exibida como certeza — a UI mostra "~X km" em
  // ambar — mas ordena a lista e serve de RECOMENDADA de ultimo recurso quando
  // nenhuma lider da cidade do lead tem coordenada confiavel. Ver
  // rankLeadersByDistance em server/routes/leaders.cjs.
  approx_distance?: number | null
  geoUnreliable?: boolean
  leader_cep?: string | null   // CEP cadastral da líder (Oracle)
  address?: string | null      // endereço cadastral completo p/ conferência da distância
  weekly_assignments?: number
  best_match?: boolean
  is_active?: boolean       // false = líder sem vendas recentes — Oracle.cadastro_clientes.ativa=0
  inactive_since?: string | null  // ex: '201812' (sem lançamento desde 12/2018)
  has_valid_phone?: boolean // false = telefone do cadastro não normaliza pra E.164 (precisa atualizar cadastro)
  ddd_assumed?: boolean     // true = DDD 67 foi assumido (cadastro veio sem DDD — caso comum em líder antiga MS)
  phone_e164?: string       // 55XXXXXXXXXXX após normalização — usado pro envio
  // Presença de WhatsApp acumulada dos handoffs reais (passivo, sem custo).
  // undefined = nunca recebeu handoff (não verificado).
  wa_status?: { has_whatsapp: boolean; last_status?: string; last_error_code?: string | null; updated_at?: string }
  // Guardrail de entrega (2026-07-07): quantas notificações de handoff falharam
  // pra essa líder nos últimos 14 dias (handoff_delivery_failures). Não impede
  // escolher — só avisa; o auto-assign já exclui automaticamente com ≥1, aqui
  // o operador decide de olhos abertos (caso real: líder escolhida às cegas
  // com 7 falhas 131049 acumuladas, o lead nunca chegou até ela).
  recentFails?: number
}

interface AssignLeaderModalProps {
  isOpen: boolean
  onClose: () => void
  conversationId: string
  deviceId: string
  contactName?: string
  contactPhone?: string
  contactRemoteJid?: string
  conversationMessages?: { content?: string | null; from_me?: number }[]
  leadExtractedData?: string | null
  onAssign: (leaderId: string, leaderData: Leader) => void
}

// Extrair localização das mensagens: tenta CEP primeiro, depois cidade/endereço
// from_me pode vir como number OU string ("0"/"1") dependendo da fonte (MySQL VARCHAR)
const isFromClient = (m: { from_me?: number | string }) => Number(m.from_me) === 0

function extractLocationFromMessages(messages: { content?: string | null; from_me?: number | string }[]): { type: 'cep'; value: string } | { type: 'address'; value: string } | null {
  const clientMessages = messages.filter(m => isFromClient(m) && m.content)

  // 1. Tentar encontrar CEP — em 3 níveis de confiança, do mais seguro ao mais genérico.
  //    Evita pegar CPF/telefone sem pontuação como CEP.
  const tryExtractCep = (cep: string): string | null => {
    const num = parseInt(cep.substring(0, 2))
    return (num >= 1 && num <= 99) ? cep : null
  }

  // 1a) Palavra "CEP" próxima: "CEP 79380-000" ou "cep: 79380000"
  for (const msg of clientMessages) {
    if (!msg.content) continue
    const m = msg.content.match(/\bcep\b[:\s]*(\d{5})[-\s]?(\d{3})/i)
    if (m) {
      const cep = tryExtractCep(m[1] + m[2])
      if (cep) return { type: 'cep', value: cep }
    }
  }

  // 1b) Hífen obrigatório: "79380-000" (formato típico). Pula números grudados
  //     como CPFs (11 dígitos) — o \b impede string de dígitos acima de 8.
  for (const msg of clientMessages) {
    if (!msg.content) continue
    const re = /(?<!\d)(\d{5})-(\d{3})(?!\d)/g
    const matches = [...msg.content.matchAll(re)]
    for (const match of matches) {
      const cep = tryExtractCep(match[1] + match[2])
      if (cep) return { type: 'cep', value: cep }
    }
  }

  // 1c) Último recurso: 8 dígitos isolados, com certeza não é parte de número maior
  //     (rejeita CPF 11 dígitos, telefone 9-11 dígitos).
  //     Também rejeita fragmentos de telefone: dígitos precedidos por outros dígitos + espaço
  //     ex: "679 91136579" — "91136579" parece CEP mas é parte de telefone.
  for (const msg of clientMessages) {
    if (!msg.content) continue
    const re = /(?<!\d)(\d{5})(\d{3})(?!\d)/g
    const matches = [...msg.content.matchAll(re)]
    for (const match of matches) {
      const before = msg.content.substring(0, match.index ?? 0)
      if (/\d\s+$/.test(before)) continue  // fragmento de telefone partido em duas partes
      const cep = tryExtractCep(match[1] + match[2])
      if (cep) return { type: 'cep', value: cep }
    }
  }

  // 2. Tentar extrair cidade e endereço das mensagens do cliente
  for (const msg of clientMessages) {
    if (!msg.content) continue
    const text = msg.content

    // Melhorar detecção de endereço e cidade
    // Padrão 1: "Endereço Rua X" ou "Endereco X"
    const enderecoMatch = text.match(/[Ee]nderec?[çc]o\s*[:.\s]*([^\n]*)/i)
    // Padrão 2: "Bairro X" (pode estar junto com endereço)
    const bairroMatch = text.match(/[Bb]airro\s*[:.\s]*([^\n]*?)(?:\.|$)/i)

    // Padrão 3: Detectar cidades conhecidas de MS
    const cidadesMS = [
      'Campo Grande', 'Dourados', 'Três Lagoas', 'Corumbá', 'Ponta Porã',
      'Naviraí', 'Nova Andradina', 'Aquidauana', 'Maracaju', 'Paranaíba'
    ]

    let cidadeEncontrada = null

    // Buscar cidade no texto
    for (const cidade of cidadesMS) {
      if (text.toLowerCase().includes(cidade.toLowerCase())) {
        cidadeEncontrada = cidade
        break
      }
    }

    // Se não encontrou cidade conhecida, tentar padrão genérico
    if (!cidadeEncontrada) {
      const cidadeMatch = text.match(/[Cc]idade[:\s]*([A-ZÀ-Úa-zà-ú][A-Za-zÀ-Úa-zà-ú\s]*?)(?:\s+[A-Z]{2})?(?:\.|,|\n|$)/u)
      if (cidadeMatch) {
        cidadeEncontrada = cidadeMatch[1].trim()
      }
    }

    // Construir endereço completo
    if (cidadeEncontrada || enderecoMatch || bairroMatch) {
      const parts = []

      // Adicionar endereço se existir
      if (enderecoMatch) {
        let endereco = enderecoMatch[1].trim()
        // Limpar se tem Bairro junto (ex: "Rua X.Bairro Y")
        endereco = endereco.replace(/\.?[Bb]airro.*/, '').trim()
        if (endereco) parts.push(endereco)
      }

      // Adicionar bairro se existir
      if (bairroMatch) {
        const bairro = bairroMatch[1].trim()
        if (bairro && !bairro.toLowerCase().includes('cidade')) {
          parts.push(bairro)
        }
      }

      // Adicionar cidade
      if (cidadeEncontrada) {
        parts.push(cidadeEncontrada + ' MS')
      }

      if (parts.length > 0) {
        const address = parts.join(', ')
        return { type: 'address', value: address }
      }
    }
  }

  return null
}

// Extrair CPF das mensagens do cliente
function extractCPFFromMessages(messages: { content?: string | null; from_me?: number | string }[]): string | null {
  const clientMessages = messages.filter(m => isFromClient(m) && m.content)

  for (const msg of clientMessages) {
    if (!msg.content) continue
    // Padrão com "CPF" antes do número
    const cpfLabelMatch = msg.content.match(/[Cc][Pp][Ff][:\s]+(\d{3}[\.\-]?\d{3}[\.\-]?\d{3}[\.\-]?\d{2})/u)
    if (cpfLabelMatch) {
      const digits = cpfLabelMatch[1].replace(/\D/g, '')
      if (digits.length === 11) return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6,9)}-${digits.slice(9,11)}`
    }
    // Padrão formatado XXX.XXX.XXX-XX
    const formatted = msg.content.match(/\b(\d{3}\.\d{3}\.\d{3}-\d{2})\b/)
    if (formatted) return formatted[1]
    // Padrão 11 dígitos consecutivos (não confundir com telefone que tem 10-11 dígitos com DDD)
    const raw = msg.content.match(/\b(\d{11})\b/g)
    if (raw) {
      for (const r of raw) {
        const d = r
        // Heurística: CPFs geralmente não começam com 55 (código do Brasil) nem com DDD comuns
        if (!d.startsWith('55')) {
          return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`
        }
      }
    }
  }
  return null
}

// Detecta nome de contato inválido (placeholder do WhatsApp profile name).
// Quando lead bota '.', '..', 'Lu', emoji puro etc. como display name na Meta,
// o webhook salva como remote_name. Esse valor não deve viajar pra líder.
function isInvalidContactName(raw?: string | null): boolean {
  if (!raw) return true
  const trimmed = raw.trim()
  if (trimmed.length < 3) return true
  // Gemma 4 às vezes serializa "null" como string (caso Divanir 2026-05-24).
  if (/^(null|undefined|none|n\/a)$/i.test(trimmed)) return true
  // O placeholder do extractClientData (linha ~223) TAMBÉM é "sem nome" — sem
  // isto, pushname só-emoji (ex.: "🥰") virava 'Não informado', o guard do
  // modal passava e o backend rejeitava com 409 (caso relatado 2026-07-10).
  if (/^não informado$/i.test(trimmed)) return true
  if (/^[\p{P}\p{S}\s]+$/u.test(trimmed)) return true
  const noPunct = trimmed.replace(/[\p{P}\p{S}]/gu, '').trim()
  if (noPunct.length < 3) return true
  return false
}

// Extrair dados completos do cliente das mensagens da conversa
// [2026-07-27] Composicao das previas extraida pra ca. Antes ela existia so
// inline no handler que abre o step de confirmacao, entao o botao "Usar <nome do
// chat>" (aviso de nome divergente) trocava clientData.name e as duas previas
// seguiam mostrando o nome velho — a operadora corrigia pra "Natalia Gomes de
// Souza" e a tela continuava dizendo "WALMIR RODRIGUES SILVA". O envio real ja
// ia certo (vai por leadNameOverride, o backend nao usa estes textos), mas uma
// previa que mente e pior que previa nenhuma: nao da pra pedir conferencia numa
// tela que nao reflete o que foi corrigido.
type LeadPreviewData = { name: string; cpf: string; bairro: string; cidade: string; endereco: string; phone: string }

function buildLeaderPreview(data: LeadPreviewData, leaderName: string, now: Date): string {
  const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''
  const bairroCidade = data.endereco !== 'Não informado'
    ? data.endereco
    : [data.bairro, data.cidade].filter(v => v !== 'Não informado').join(', ') || 'Não informado'
  const leaderFirstName = capitalize((leaderName || '').split(' ')[0])
  const hour = now.getHours()
  const saudacao = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite'
  const formatPhone = (p: string) => {
    const d = (p || '').replace(/\D/g, '')
    if (d.startsWith('55') && d.length >= 12) {
      return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`
    }
    return p
  }
  // Sem ameaça política do "caso contrário vai pra outra líder" — a regra
  // interna de reatribuição existe mas não se anuncia pra líder destinatária.
  return `${saudacao} ${leaderFirstName}, tudo bem!? Estou encaminhando um contato da ação de tráfego pago, poderia atender por favor?

*Nome:* ${data.name}
*CPF:* ${data.cpf}
*Endereço:* ${bairroCidade}
*Telefone:* ${formatPhone(data.phone)}

Conto com seu retorno em até 24hrs. Obrigada!`
}

// O bot já confirma dados e avisa "uma líder vai te chamar" no turno anterior —
// aqui personalizamos com o nome real da líder pra evitar repetição genérica.
function buildClientPreview(leadName: string, leaderName: string): string {
  const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''
  const capitalizeName = (s: string) => s.toLowerCase().replace(/\b([a-zà-ÿ])/g, (m) => m.toUpperCase())
  const leadFirstName = capitalize((leadName || '').split(' ')[0])
  const greet = leadFirstName ? `Pronto, ${leadFirstName}! ` : 'Pronto! '
  return `${greet}A líder *${capitalizeName(leaderName || '')}* vai te chamar pelo celular pessoal dela em até 24h pra finalizar seu cadastro como consultora. Fique de olho no WhatsApp! ❤️`
}

// [2026-07-27] Nome que a PESSOA DIGITOU no chat — distinto do pushname do
// WhatsApp (dono da linha) e do nome do cadastro (quem paga a linha). Numa linha
// de familia essas tres coisas sao tres pessoas: caso Natalia (lead) x Clecy
// (cadastro canonico) x Walmir (dono do celular), 24/07.
//
// Aceita SO evidencia forte, deliberadamente. A heuristica antiga de "qualquer
// linha com 2+ palavras e um nome" casaria com "Quero vender" / "Como faco",
// que e exatamente o que a lead manda antes de dizer o nome. Duas fontes:
//   1. auto-declaracao ("meu nome e X", "me chamo X")
//   2. resposta direta a quem PERGUNTOU o nome — bot ou atendente humano
// Vence a mais completa (mais palavras): "Natalia Gomes de Souza" > "Natalia".
//
// NAO sobrescreve nada sozinho: o resultado so alimenta o aviso de divergencia
// no modal, e quem decide e a operadora. Ver AVISO DE NOME DIVERGENTE abaixo.
function extractNameDeclaredInChat(
  messages: { content?: string | null; from_me?: number | string }[]
): string | null {
  const W = '[A-ZÀ-Úa-zà-ú][A-Za-zÀ-ÿ]*'
  const NAME_RE = new RegExp(`^(${W}(?:\\s+(?:da|de|do|das|dos|e)\\s+|\\s+)${W}(?:\\s+${W}){0,4})$`, 'u')
  const ASKS_NAME = /(?:seu\s+nome|nome\s+completo|qual\s+(?:é\s+)?(?:o\s+)?seu\s+nome|como\s+(?:você\s+)?se\s+chama)/i
  const SELF_DECL = new RegExp(
    `(?:meu\\s+nome\\s+(?:é|e|eh)|me\\s+chamo)\\s+(${W}(?:\\s+(?:da|de|do|das|dos|e)\\s+|\\s+)?${W}?(?:\\s+${W}){0,3})`,
    'iu'
  )
  const candidates: string[] = []

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]
    if (!msg.content || !isFromClient(msg)) continue

    const decl = msg.content.match(SELF_DECL)
    if (decl && decl[1]) candidates.push(decl[1].trim())

    // Olha a ultima mensagem NOSSA imediatamente anterior: se ela pediu o nome,
    // esta resposta e o nome. Cobre o fluxo humano (a atendente digita "Seu nome
    // completo") tanto quanto o do bot.
    for (let j = i - 1; j >= 0 && j >= i - 3; j--) {
      const prev = messages[j]
      if (!prev.content || isFromClient(prev)) continue
      if (ASKS_NAME.test(prev.content)) {
        const lines = msg.content.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
        for (const ln of lines) {
          const nm = ln.match(NAME_RE)
          if (nm && nm[1]) { candidates.push(nm[1].trim()); break }
        }
      }
      break
    }
  }

  const valid = candidates.filter(c => c && !isInvalidContactName(c))
  if (!valid.length) return null
  valid.sort((a, b) => b.split(/\s+/).length - a.split(/\s+/).length)
  return valid[0]
}

function extractClientDataFromMessages(
  messages: { content?: string | null; from_me?: number | string }[],
  contactName?: string,
  contactPhone?: string
): { name: string; cpf: string; bairro: string; cidade: string; endereco: string; phone: string; cep: string; nameFromChat: string | null } {
  let name = isInvalidContactName(contactName) ? 'Não informado' : contactName!.trim()
  let cpf = 'Não informado'
  let bairro = 'Não informado'
  let cidade = 'Não informado'
  let endereco = 'Não informado'
  let cep = ''
  const phone = contactPhone || 'Não informado'
  // Evidencia independente do pushname/cadastro — ver extractNameDeclaredInChat.
  const nameFromChat = extractNameDeclaredInChat(messages)

  // PRIORIDADE 1: Extrair do resumo estruturado do bot (formato mais confiável)
  // O bot gera resumos com formato: *Nome:* valor / *CPF:* valor / *Endereço:* valor / *CEP:* valor
  // (resumos podem vir só com CEP quando o lead informou CEP em vez de endereço completo)
  const isFromBot = (m: { from_me?: number | string }) => Number(m.from_me) === 1
  const resumoMessages = messages.filter(m => m.content && isFromBot(m) &&
    m.content.includes('Nome:') && m.content.includes('CPF:') &&
    (m.content.includes('Endereço:') || m.content.includes('CEP:') ||
     m.content.includes('Resumo do cadastro') || m.content.includes('Resumo do Cadastro')))

  if (resumoMessages.length > 0) {
    // Usar o resumo mais recente
    const resumo = resumoMessages[resumoMessages.length - 1].content!

    // [2026-06-16] Parado em [^*\n] em vez de [^\n]: o Moss/Clara manda o resumo
    // em linha única ("*Nome:* X *Telefone:* Y *CPF:* Z...") — o * sinaliza início
    // do próximo campo bold e delimita o valor atual. [^\n] capturava o resto todo.
    const nomeMatch = resumo.match(/\*?Nome:\*?\s*([^*\n]+)/i)
    // Só sobrescreve se o valor extraído passar no validador — sem isso, o resumo
    // do bot serializando "*Nome:* *" (caso real 2026-05-26 8:35 Alessandra) vazava
    // como nome do lead e a mensagem virava "Pronto, *!".
    if (nomeMatch && !isInvalidContactName(nomeMatch[1])) name = nomeMatch[1].trim()

    const cpfMatch = resumo.match(/\*?CPF:\*?\s*([^*\n]+)/i)
    if (cpfMatch) cpf = cpfMatch[1].trim()

    const telMatch = resumo.match(/\*?Telefone:\*?\s*([^*\n]+)/i)
    // phone já vem do contactPhone, mas se o resumo tiver um melhor, usar

    const endMatch = resumo.match(/\*?Endereço:\*?\s*([^*\n]+)/i)
    if (endMatch) {
      endereco = endMatch[1].trim()
      // Extrair bairro e cidade do endereço completo
      // Formato típico: "Rua X, 123, Bairro Y, Cidade - UF"
      const parts = endereco.split(',').map(p => p.trim())
      if (parts.length >= 3) {
        // Última parte geralmente é "Cidade - UF"
        const cidadePart = parts[parts.length - 1].trim()
        cidade = cidadePart
        // Penúltima parte geralmente é o bairro
        const bairroPart = parts[parts.length - 2].trim()
        // Remover prefixo "Bairro" se existir
        bairro = bairroPart.replace(/^bairro\s+/i, '')
      } else if (parts.length === 2) {
        cidade = parts[1].trim()
        bairro = parts[0].trim()
      }
    }

    const cepMatch = resumo.match(/\*?CEP:\*?\s*([\d][\d\-\.]+)/i)
    if (cepMatch) cep = cepMatch[1].replace(/\D/g, '')

    return { name, cpf, bairro, cidade, endereco, phone, cep, nameFromChat }
  }

  // PRIORIDADE 2: Fallback — extrair das mensagens cruas do cliente.
  // Lead frequentemente manda um pacote em uma única mensagem com várias
  // linhas: "Rua X 123\nBairro Y\nCidade UF". Tratamos linha-a-linha pra
  // capturar street + bairro + cidade quando vêm separados.
  cpf = extractCPFFromMessages(messages) || 'Não informado'

  const clientMessages = messages.filter(m => isFromClient(m) && m.content)
  const cityUfRegex = /^([A-ZÀ-Ú][a-zà-úA-ZÀ-Ú\s']+?)\s*[-\/,\s]+\s*(MS|MT|GO|SP|RJ|MG|PR|RS|SC|BA|PE|CE|PA|MA|PB|RN|AL|SE|PI|RO|AC|AM|RR|AP|TO|DF|ES)\b/u
  const isStreetLine = (s: string) => /^(rua|r\.|av\.?|avenida|travessa|tv\.?|alameda|al\.?|estrada|rodovia|rod\.?|praça|pç\.?|largo)\b/i.test(s.trim())

  for (const msg of clientMessages) {
    if (!msg.content) continue
    const text = msg.content
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean)

    // Identificar linha-âncora de rua/avenida e tratar as próximas como bairro/cidade
    const streetIdx = lines.findIndex(isStreetLine)
    if (streetIdx >= 0) {
      if (endereco === 'Não informado') endereco = lines[streetIdx]
      // Próximas 3 linhas viram candidatas a bairro/cidade
      for (let j = streetIdx + 1; j < Math.min(streetIdx + 4, lines.length); j++) {
        const ln = lines[j]
        if (!ln || /^\d{4,}$/.test(ln)) continue // pula CEP/números soltos
        const cityM = ln.match(cityUfRegex)
        if (cityM && cidade === 'Não informado') {
          cidade = `${cityM[1].trim()} - ${cityM[2]}`
        } else if (bairro === 'Não informado' && ln.length <= 60 && !cityM) {
          // Linha curta sem padrão de cidade → bairro
          bairro = ln.replace(/^bairro\s+/i, '').trim()
        }
      }
    } else {
      // Mesmo sem linha-âncora, varrer cada linha por padrão de cidade
      for (const ln of lines) {
        if (cidade === 'Não informado') {
          const cityM = ln.match(cityUfRegex)
          if (cityM) cidade = `${cityM[1].trim()} - ${cityM[2]}`
        }
      }
    }

    // Buscar CEP solto (8 dígitos com ou sem hífen)
    if (!cep) {
      const cepMatch = text.match(/\b(\d{5})[-\s]?(\d{3})\b/)
      if (cepMatch) cep = cepMatch[1] + cepMatch[2]
    }

    // Buscar nome em mensagem do tipo "Fulano de Tal" (linha só com nome)
    if (name === 'Não informado') {
      for (const ln of lines) {
        const lineNameMatch = ln.match(/^([A-ZÀ-Úa-zà-ú]+(?:\s+(?:da|de|do|das|dos|e)\s+|\s+)[A-ZÀ-Úa-zà-ú]+(?:\s+[A-ZÀ-Úa-zà-ú]+){0,4})$/u)
        if (lineNameMatch && lineNameMatch[1].split(/\s+/).length >= 2 && !isStreetLine(ln)) {
          name = lineNameMatch[1].trim()
          break
        }
      }
    }
  }

  // Se temos street solto + bairro/cidade soltos, concatenar num endereço
  // completo no formato esperado pelo template do líder.
  if (endereco !== 'Não informado' && !endereco.includes(',')) {
    const partes = [endereco, bairro !== 'Não informado' ? bairro : null, cidade !== 'Não informado' ? cidade : null].filter(Boolean)
    if (partes.length > 1) endereco = partes.join(', ')
  }

  // PRIORIDADE 3: Saudação do bot ("Olá Jenifer, ..." / "Jenifer, ainda por aqui?")
  // Bot Clara sempre cumprimenta com primeiro nome. Se ainda não temos nome,
  // pega o mais frequente nas saudações do bot — sinal forte que o bot já
  // identificou o lead pelo nome real, mesmo sem ter gerado um resumo.
  if (name === 'Não informado') {
    const botMessages = messages.filter(m => isFromBot(m) && m.content)
    const candidates: Record<string, number> = {}
    for (const msg of botMessages) {
      if (!msg.content) continue
      const m = msg.content.match(/^(?:Olá|Oi)\s+([A-ZÀ-Ú][\wà-úÀ-Ú]+(?:\s+[A-ZÀ-Ú][\wà-úÀ-Ú]+){0,4})[!,\s]/u)
        || msg.content.match(/^([A-ZÀ-Ú][\wà-úÀ-Ú]+),\s/u)
      if (m && m[1]) {
        const cand = m[1].trim()
        if (cand.length >= 3) candidates[cand] = (candidates[cand] || 0) + 1
      }
    }
    // Prefere nome com mais palavras (completo > primeiro nome só); desempate
    // por frequência. "Jenifer Lima Dizengrini" (1x) ganha de "Jenifer" (3x).
    const ranked = Object.entries(candidates).sort((a, b) => {
      const wa = a[0].split(/\s+/).length
      const wb = b[0].split(/\s+/).length
      if (wa !== wb) return wb - wa
      return b[1] - a[1]
    })
    if (ranked.length > 0) name = ranked[0][0]
  }

  return { name, cpf, bairro, cidade, endereco, phone, cep, nameFromChat }
}

// Resolve um CEP em endereço completo via /api/cep — usado quando o lead
// só informou CEP (sem rua/bairro). Retorna texto pronto para o template do líder.
// Fallback: se o CEP exato não existir na base (typo do lead — caso real
// Maria Estela 79062-244), tenta o CEP genérico `<prefix5>000` (cobre o
// bairro/cidade da mesma faixa) e marca o resultado como aproximado.
async function resolveCepToAddress(
  cep: string,
  authHeaders: () => Record<string, string>
): Promise<{ endereco: string; bairro: string; cidade: string; approximate?: boolean } | null> {
  const clean = (cep || '').replace(/\D/g, '')
  if (clean.length !== 8) return null
  const fetchOne = async (c: string) => {
    try {
      const resp = await fetch(`/api/cep/${c}`, { headers: authHeaders() })
      if (!resp.ok) return null
      return await resp.json()
    } catch {
      return null
    }
  }
  let data = await fetchOne(clean)
  let approximate = false
  if (!data) {
    const generic = clean.slice(0, 5) + '000'
    if (generic !== clean) {
      data = await fetchOne(generic)
      if (data) approximate = true
    }
  }
  if (!data) return null
  const street = (data.street || '').trim()
  const neighborhood = (data.neighborhood || '').trim()
  const city = (data.city || '').trim()
  const state = (data.state || '').trim()
  if (!city && !neighborhood && !street) return null
  const cidadeUf = state ? `${city} - ${state}` : city
  // No fallback aproximado a rua não corresponde ao CEP do lead — omitimos
  // pra não passar endereço falso pra líder.
  const partes = approximate
    ? [neighborhood, cidadeUf].filter(Boolean)
    : [street, neighborhood, cidadeUf].filter(Boolean)
  return {
    endereco: partes.join(', ') || 'Não informado',
    bairro: neighborhood || 'Não informado',
    cidade: cidadeUf || 'Não informado',
    approximate
  }
}

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth-token')}`
})

export default function AssignLeaderModal({
  isOpen,
  onClose,
  conversationId,
  deviceId,
  contactName,
  contactPhone,
  contactRemoteJid,
  conversationMessages,
  leadExtractedData,
  onAssign
}: AssignLeaderModalProps) {
  // Nome a exibir nos cabeçalhos do modal. Prioridade:
  //   1. lead_extracted_data.nome — resposta direta do lead à pergunta do bot
  //   2. contactName (pushname WhatsApp) — só se não for placeholder/abreviação
  //   3. fallback "Sem nome"
  // Extracted vence pushname porque pushname pode ser "CRBA"/"Mhy❤️" etc.,
  // enquanto extracted veio da pergunta "qual seu nome completo?".
  const displayName = useMemo(() => {
    if (leadExtractedData) {
      try {
        const parsed = typeof leadExtractedData === 'string' ? JSON.parse(leadExtractedData) : leadExtractedData
        const n = parsed?.nome || parsed?.name
        if (n && !isInvalidContactName(String(n))) return String(n).trim()
      } catch { /* ignora JSON inválido */ }
    }
    if (contactName && !isInvalidContactName(contactName)) return contactName.trim()
    return 'Sem nome'
  }, [contactName, leadExtractedData])

  const [leaders, setLeaders] = useState<Leader[]>([])
  const [filteredLeaders, setFilteredLeaders] = useState<Leader[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [searchCEP, setSearchCEP] = useState('')
  const [loading, setLoading] = useState(false)
  const [calculatingDistance, setCalculatingDistance] = useState(false)
  const [selectedLeader, setSelectedLeader] = useState<Leader | null>(null)
  const [cepInfo, setCepInfo] = useState<string | null>(null)
  // Endereço geocodificado do cliente — é a origem do cálculo de distância.
  // Exibido em destaque pra operador comparar com o endereço de cada líder.
  const [clientLocation, setClientLocation] = useState<{ street?: string | null; neighborhood?: string | null; city?: string | null; state?: string | null; geocodedBy?: string | null; confidence?: string | null; distanceReliable?: boolean; warning?: string | null } | null>(null)
  const [autoSearchDone, setAutoSearchDone] = useState(false)

  // Confirmation step state
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [leaderMessage, setLeaderMessage] = useState('')
  const [clientMessage, setClientMessage] = useState('')
  const [clientData, setClientData] = useState<{ name: string; cpf: string; bairro: string; cidade: string; endereco: string; phone: string; cep: string; nameFromChat: string | null } | null>(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  // Backend bloqueia reatribuir um contato que já tem líder ativa diferente
  // (evita mandar o mesmo lead pra duas líderes). No 2º clique em Confirmar
  // mandamos reassignConfirmed=true pra prosseguir conscientemente.
  const [reassignConfirmed, setReassignConfirmed] = useState(false)
  // [2026-07-27] Backend agora BARRA (409) handoff em numero compartilhado por
  // 2+ cadastros quando o nome nao veio digitado pela operadora — antes so
  // avisava, DEPOIS de ja ter mandado o nome errado pra lider (caso Natalia).
  // Guardamos os donos da linha pra mostrar quem sao e liberar o campo de nome.
  const [sharedNumberBlocked, setSharedNumberBlocked] = useState(false)
  const [sharedNumberOwners, setSharedNumberOwners] = useState<string[]>([])
  const [sharedNumberConfirmed, setSharedNumberConfirmed] = useState(false)
  // Quem recebe mensagem nesse envio. 'both' é o caminho normal; 'leader_only'
  // e 'lead_only' são pra retry/recovery — operador já mandou pro outro lado
  // e quer evitar mensagem duplicada (queima de template / "Pronto" repetido).
  const [sendTarget, setSendTarget] = useState<'both' | 'leader_only' | 'lead_only'>('both')

  // Busca líderes do banco
  useEffect(() => {
    if (isOpen) {
      fetchLeaders()
      setAutoSearchDone(false)
      setClientLocation(null)
      setCepInfo(null)
      // Reset confirmation state
      setShowConfirmation(false)
      setLeaderMessage('')
      setClientMessage('')
      setSending(false)
      setSendError(null)
      setReassignConfirmed(false)
      setSendTarget('both')
    }
  }, [isOpen])

  const fetchLeaders = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/leaders', { headers: authHeaders() })
      if (response.ok) {
        const data = await response.json()
        setLeaders(data)
        setFilteredLeaders(data)
      }
    } catch (error) {
      console.error('Erro ao buscar líderes:', error)
    } finally {
      setLoading(false)
    }
  }

  // Busca por endereço (cidade/rua) via Google Geocoding
  const handleAddressSearch = async (address: string) => {
    if (!address || address.length < 3) return

    setCalculatingDistance(true)
    setCepInfo(null)
    setClientLocation(null)
    try {
      const response = await fetch(`/api/leaders/nearby-address?address=${encodeURIComponent(address)}`, {
        headers: authHeaders()
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        setCepInfo(err.error || 'Erro ao buscar por endereço')
        return
      }

      const data = await response.json()

      if (data.clientLocation) setClientLocation(data.clientLocation)

      setLeaders(data.leaders)
      setFilteredLeaders(data.leaders)
      setSearchTerm('')
    } catch (error) {
      console.error('Erro ao buscar por endereço:', error)
      setCepInfo('Erro de conexão')
    } finally {
      setCalculatingDistance(false)
    }
  }

  // Auto-busca: detectar CEP ou endereço nas mensagens e buscar automaticamente.
  // Se o caller não passar conversationMessages, busca do endpoint — garante
  // que o auto-search funcione vindo de qualquer lugar da UI.
  useEffect(() => {
    if (!isOpen || autoSearchDone || loading) return
    if (leaders.length === 0) return

    const run = async () => {
      // PRIORIDADE 1: CEP já salvo pelo chatbot em lead_extracted_data
      if (leadExtractedData) {
        try {
          const parsed = typeof leadExtractedData === 'string' ? JSON.parse(leadExtractedData) : leadExtractedData
          // Gemma 4 às vezes serializa "null" como string. Pula esses casos.
          const isNullish = (v: unknown) => v == null || /^(null|undefined|none|n\/a)$/i.test(String(v).trim())
          if (parsed?.cep && !isNullish(parsed.cep)) {
            const cep = String(parsed.cep).replace(/\D/g, '')
            if (cep.length === 8) {
              setSearchCEP(cep)
              setAutoSearchDone(true)
              handleCEPSearchWithCep(cep)
              return
            }
          }
          // Sem CEP mas tem endereço — tenta geocoding pelo endereço salvo
          if (parsed?.endereco && !isNullish(parsed.endereco)) {
            setAutoSearchDone(true)
            handleAddressSearch(parsed.endereco)
            return
          }
        } catch (_) {}
      }

      // PRIORIDADE 2: Extrair das mensagens da conversa
      let msgs = conversationMessages
      if ((!msgs || msgs.length === 0) && conversationId) {
        try {
          const resp = await fetch(`/api/whatsapp-messages/${conversationId}?limit=200`, { headers: authHeaders() })
          if (resp.ok) {
            const data = await resp.json()
            msgs = Array.isArray(data) ? data : (data?.messages || [])
          }
        } catch (err) {
          console.error('[AssignLeader/AutoSearch] falha ao carregar mensagens:', err)
        }
      }

      if (!msgs || msgs.length === 0) {
        setAutoSearchDone(true)
        return
      }

      const location = extractLocationFromMessages(msgs)

      if (location) {
        if (location.type === 'cep') {
          setSearchCEP(location.value)
          setAutoSearchDone(true)
          handleCEPSearchWithCep(location.value)
        } else {
          setAutoSearchDone(true)
          handleAddressSearch(location.value)
        }
      } else {
        setAutoSearchDone(true)
      }
    }

    run()
  }, [isOpen, leaders, loading, autoSearchDone, conversationMessages, conversationId])

  // Filtra líderes por nome ou código. Independente de busca textual ou
  // proximidade, o líder marcado como best_match (golden, sugerido pelo
  // backend com base em distância+carga) sempre vai pro topo da lista —
  // operador precisa vê-lo sem precisar rolar.
  useEffect(() => {
    const base = searchTerm
      ? leaders.filter(leader => {
          const term = searchTerm.toLowerCase()
          return leader.name.toLowerCase().includes(term) ||
                 leader.code.includes(searchTerm) ||
                 leader.group_name?.toLowerCase().includes(term)
        })
      : leaders
    const sorted = [...base].sort((a, b) => {
      if (a.best_match && !b.best_match) return -1
      if (!a.best_match && b.best_match) return 1
      return 0
    })
    setFilteredLeaders(sorted)
  }, [searchTerm, leaders])

  // Busca por proximidade via backend (Google Geocoding) - aceita CEP como parâmetro
  const handleCEPSearchWithCep = async (cep: string) => {
    if (!cep || cep.length < 8) return

    setCalculatingDistance(true)
    setCepInfo(null)
    setClientLocation(null)
    try {
      const response = await fetch(`/api/leaders/nearby/${cep}`, {
        headers: authHeaders()
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        setCepInfo(err.error || 'Erro ao buscar por CEP')
        return
      }

      const data = await response.json()

      // Endereço geocodificado vai pro box destacado; cepInfo guarda só o aviso
      // de aproximação quando o CEP exato não existe na base.
      if (data.clientLocation) setClientLocation(data.clientLocation)
      if (data.cepFallback) {
        const fb = data.cepFallback
        setCepInfo(`⚠️ CEP ${fb.requested.replace(/(\d{5})(\d{3})/, '$1-$2')} não existe na base — usando aproximação via ${fb.used.replace(/(\d{5})(\d{3})/, '$1-$2')}.`)
      } else if (data.bestMatchApproximate) {
        // [2026-07-27] Nenhuma líder ativa da cidade do lead tem coordenada
        // confiável, então a RECOMENDADA saiu das aproximadas. Antes disso a
        // líder local simplesmente não aparecia como próxima e a sugestão ia
        // pra outra cidade — o bug do CEP. Aviso em vez de falsa precisão.
        setCepInfo('⚠️ Nenhuma líder desta cidade tem endereço geocodificado com precisão. A sugestão usa distância aproximada (coordenada do centro da cidade) — confira o endereço da líder antes de enviar.')
      }

      // Atualizar lista com distâncias do backend
      setLeaders(data.leaders)
      setFilteredLeaders(data.leaders)
      setSearchTerm('') // Limpar filtro de texto para mostrar ordenação por distância
    } catch (error) {
      console.error('Erro ao buscar por proximidade:', error)
      setCepInfo('Erro de conexão')
    } finally {
      setCalculatingDistance(false)
    }
  }

  // Wrapper que usa o state searchCEP
  const handleCEPSearch = () => handleCEPSearchWithCep(searchCEP)

  // Step 1 → Step 2: Prepara as mensagens e mostra tela de confirmação
  const handleAssign = async () => {
    if (!selectedLeader) return

    // Extrair dados do cliente das mensagens
    const extractedData = extractClientDataFromMessages(
      conversationMessages || [],
      contactName,
      contactPhone
    )

    // Fallback: lead só informou CEP (sem rua/bairro). Resolver CEP em endereço
    // completo via BrasilAPI antes de montar a mensagem ao líder.
    let cepCandidate = extractedData.cep
    if (!cepCandidate && leadExtractedData) {
      try {
        const parsed = typeof leadExtractedData === 'string' ? JSON.parse(leadExtractedData) : leadExtractedData
        if (parsed?.cep) cepCandidate = String(parsed.cep).replace(/\D/g, '')
      } catch (_) {}
    }
    if (!cepCandidate && searchCEP) cepCandidate = searchCEP.replace(/\D/g, '')

    // Resolve CEP em 2 cenários:
    //  (a) endereço totalmente ausente → preenche tudo via BrasilAPI
    //  (b) endereço parcial (só rua, sem bairro/cidade) → completa com o que
    //      a API retornar; preserva os dados que o lead já forneceu.
    const enderecoIncompleto = extractedData.endereco === 'Não informado'
      || (!extractedData.endereco.includes(',')
          && extractedData.bairro === 'Não informado'
          && extractedData.cidade === 'Não informado')
    if (enderecoIncompleto && cepCandidate) {
      const resolved = await resolveCepToAddress(cepCandidate, authHeaders)
      if (resolved) {
        if (extractedData.endereco === 'Não informado') {
          extractedData.endereco = resolved.endereco
        } else {
          // Mesclar: rua do lead + bairro/cidade do CEP
          const partes = [extractedData.endereco]
          if (resolved.bairro !== 'Não informado') partes.push(resolved.bairro)
          if (resolved.cidade !== 'Não informado') partes.push(resolved.cidade)
          extractedData.endereco = partes.join(', ')
        }
        if (extractedData.bairro === 'Não informado') extractedData.bairro = resolved.bairro
        if (extractedData.cidade === 'Não informado') extractedData.cidade = resolved.cidade
      }
    }

    // Fallback de nome: lead bota '.', '..', emoji etc. como WhatsApp profile name.
    // Se o resumo do bot e a saudação não tiveram nome, busca em
    // lead_extracted_data.nome (CRM-Intelligence persiste lá).
    if (extractedData.name === 'Não informado' && leadExtractedData) {
      try {
        const parsed = typeof leadExtractedData === 'string' ? JSON.parse(leadExtractedData) : leadExtractedData
        if (parsed?.nome && !isInvalidContactName(parsed.nome)) {
          extractedData.name = String(parsed.nome).trim()
        }
      } catch (_) {}
    }

    setClientData(extractedData)
    const clientData = extractedData

    // Montar mensagem para o líder
    const leaderMsg = buildLeaderPreview(clientData, selectedLeader.name, new Date())
    const clientMsg = buildClientPreview(clientData.name || displayName || '', selectedLeader.name)

    setLeaderMessage(leaderMsg)
    setClientMessage(clientMsg)
    setSendError(null)
    setShowConfirmation(true)
  }

  // [2026-07-27] Trocar de líder reseta o toggle de destinatário. `sendTarget`
  // só era resetado na ABERTURA do modal: quem fazia um retry com "Só Líder",
  // voltava ao passo 1 e escolhia outra líder seguia com o toggle marcado — e a
  // lead não era avisada, sem nada na tela denunciando. Silencioso e sem retry:
  // o "Pronto, X!" simplesmente nunca saía.
  const selectLeader = (leader: Leader) => {
    setSelectedLeader(leader)
    setSendTarget('both')
  }

  // Step 2: Voltar para seleção de líder
  const handleBackToSelection = () => {
    setShowConfirmation(false)
    setSendError(null)
    setReassignConfirmed(false)
  }

  // [2026-07-27] Extraido do onChange do input pra ser reusado pelo botao
  // "Usar <nome do chat>" do aviso de nome divergente. Trocar o nome aqui faz o
  // modal mandar leadNameOverride — que e justamente o que destrava o gate de
  // numero compartilhado no backend.
  const applyLeadName = (v: string) => {
    if (!clientData) return
    const updated = { ...clientData, name: v }
    setClientData(updated)
    if (!selectedLeader) return
    // Recompõe AS DUAS prévias. Antes só a do cliente era refeita — a da líder
    // continuava com o nome antigo, que é o que a operadora reportou vendo.
    setLeaderMessage(buildLeaderPreview(updated, selectedLeader.name, new Date()))
    setClientMessage(buildClientPreview(v || displayName || '', selectedLeader.name))
  }

  // Step 2: Confirmar e enviar tudo
  //
  // 2026-05-26: substituído tripla {assign-leader + whatsapp-send-template +
  // whatsapp-send} por POST único atômico /handoff-leader. Motivação: o frontend
  // silenciava erros (modal fechava em 3s mesmo com errors[] populado) e leads
  // ficavam sem notificação quando janela 24h estava fechada (Jandiele, Josimara).
  // O backend agora resolve lead_name/remote_jid do banco, valida janela, executa
  // os passos em ordem e devolve status detalhado. Modal só fecha se requires_attention=false.
  const handleConfirmAndSend = async () => {
    if (!selectedLeader) return

    setSending(true)
    setSendError(null)

    try {
      const resp = await fetch(`/api/conversations/${encodeURIComponent(conversationId)}/handoff-leader`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          deviceId,
          leaderId: selectedLeader.id,
          // Backend resolve nome do banco (cascata extracted.nome → nomeCadastro →
          // remote_name validado). Só mandamos override quando for um nome REAL —
          // NUNCA o pushname do WhatsApp (= contactName): a extração semeia `name`
          // com o pushname quando não acha resumo do bot, e mandá-lo como override
          // mascarava a cascata correta (incidente Solange→"César Samuel", 2026-06-17).
          leadNameOverride: (() => {
            const n = clientData?.name?.trim()
            if (!n || n === 'Não informado' || isInvalidContactName(n)) return undefined
            if (contactName && n === contactName.trim()) return undefined  // é o pushname → deixa o backend decidir
            return n
          })(),
          // Toggle de destinatário: pula o lado que o operador desmarcou no
          // step de confirmação (caso "só líder" / "só lead" pra retry).
          skipLeaderTemplate: sendTarget === 'lead_only',
          skipLeadNotify: sendTarget === 'leader_only',
          // Mesma regra do auto-assign: o lead só ouve o nome depois que a Meta
          // confirmar que a LÍDER recebeu. Antes disto a atribuição manual
          // avisava o lead no mesmo request, então um template que falhasse
          // minutos depois deixava o lead esperando alguém que nunca soube dele.
          //
          // Exceção 'lead_only': ali o operador está justamente reenviando só o
          // aviso ao lead (o template já foi antes). Como não há template neste
          // request, não há entrega a aguardar — deferir travaria o envio.
          deferLeadNotify: sendTarget !== 'lead_only',
          handoffAttempt: 1,
          handoffTriedCodes: selectedLeader.code ? [String(selectedLeader.code)] : [],
          // [2026-07-27] CEP que a operadora esta vendo na tela. Sem isto o
          // backend so tinha o CEP do CADASTRO da linha — no caso Natalia o da
          // Clecy (79450000), CEP generico que o ViaCEP rejeita e vira
          // "Mato Grosso do Sul" no template da lider. O CEP real (79450029)
          // estava no chat e no modal, mas nunca saia daqui.
          leadCepOverride: (() => {
            const c = String(clientData?.cep || '').replace(/\D/g, '')
            return c.length === 8 ? c : undefined
          })(),
          // Reatribuição consciente (2º clique após o bloqueio do backend).
          reassignConfirmed,
          // Idem pro bloqueio de nome não-confirmado (nome veio do cadastro da
          // linha / perfil do WhatsApp em vez do que a pessoa disse no chat).
          sharedNumberConfirmed,
          leadNameConfirmed: sharedNumberConfirmed
        })
      })

      // Backend retorna 200 em sucesso total, 207 em sucesso parcial (requires_attention),
      // 4xx em validação que impede o handoff todo.
      const payload: {
        assigned?: boolean
        leaderTemplateSent?: boolean
        leadNotified?: boolean
        // 'aguardando_entrega' = defer armado, o webhook de status da Meta
        // dispara o aviso ao lead quando confirmar a entrega pra líder.
        // 'template_falhou' = não há entrega a esperar; ninguém será avisado.
        leadNotifyMethod?: 'text' | 'template' | 'aguardando_entrega' | 'template_falhou' | 'skipped' | 'skipped_duplicate' | null
        metaWindowOpen?: boolean
        hoursSinceLastInbound?: number | null
        leadName?: string
        leaderName?: string
        requires_attention?: boolean
        reassign_blocked?: boolean
        current_leader?: string
        shared_number?: boolean
        shared_number_blocked?: boolean
        shared_number_owners?: string[]
        lead_name_unconfirmed?: boolean
        lead_name_source?: string
        errors?: string[]
        steps?: { step: string; ok: boolean; detail?: unknown }[]
      } = await resp.json().catch(() => ({}))

      setSending(false)

      // Reatribuição bloqueada: arma o flag pra que o próximo clique em Confirmar
      // prossiga conscientemente. Mostra o aviso e não fecha o modal.
      if (payload.reassign_blocked) {
        setReassignConfirmed(true)
        setSendError(
          (payload.errors && payload.errors.length > 0
            ? payload.errors.join('\n')
            : `Contato já atribuído a ${payload.current_leader || 'outra líder'}.`)
        )
        return
      }

      // [2026-07-27] Numero compartilhado barrado: libera o campo de nome e
      // arma o 2o clique. Nada foi enviado ainda — o backend retorna antes de
      // atribuir, de mandar template pra lider e de notificar a lead.
      if (payload.shared_number_blocked || payload.lead_name_unconfirmed) {
        setSharedNumberBlocked(true)
        setSharedNumberOwners(payload.shared_number_owners || [])
        setSharedNumberConfirmed(true)
        setSendError(
          (payload.errors && payload.errors.length > 0 ? payload.errors.join('\n') : 'Número compartilhado por vários cadastros.') +
          '\n\nNada foi enviado. Confira o nome abaixo e clique em Confirmar de novo.'
        )
        return
      }

      if (payload.assigned && selectedLeader) {
        // Atualiza UI otimisticamente assim que a atribuição foi salva, mesmo
        // que algum passo posterior tenha falhado.
        onAssign(selectedLeader.id, selectedLeader)
      }

      if (payload.requires_attention || !resp.ok) {
        // Não fechar modal. Operador precisa ver o que falhou e decidir.
        const errs = payload.errors && payload.errors.length > 0
          ? payload.errors
          : [`Erro ${resp.status} ao processar atribuição`]
        // O aviso ao lead agora é diferido: "não notificada" pode significar
        // "aguardando a confirmação da líder", que é o comportamento correto e
        // não um erro. Distinguir os dois evita alarme falso no operador.
        const leadStatus = payload.leadNotified
          ? '✓ Lead notificada'
          : payload.leadNotifyMethod === 'aguardando_entrega'
            ? '⏳ Lead avisada assim que a líder confirmar'
            : '✗ Lead NÃO notificada'
        const summary = [
          payload.assigned ? '✓ Líder atribuída' : '✗ Atribuição',
          payload.leaderTemplateSent ? '✓ Template enviado pra líder' : '✗ Template pra líder',
          leadStatus
        ].join(' · ')
        setSendError(`${summary}\n\n${errs.join('\n')}`)
        return
      }

      // Sucesso completo: fecha.
      onClose()
    } catch (err) {
      setSending(false)
      setSendError(`Erro de conexão: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  if (!isOpen) return null

  // [2026-07-27] Rodapés viraram variáveis porque o SidePanel os recebe como prop
  // e os prende FORA da área rolável. Antes eram só mais um filho do flex-col:
  // com a lista de líderes longa, os botões rolavam junto e sumiam da vista.
  const footerStep1 = (
    <div className="p-4 flex justify-between items-center gap-3">
      <div className="min-w-0">
        {selectedLeader && (
          <p className="text-sm text-muted-foreground truncate">
            Selecionada: <strong className="text-purple-500">{selectedLeader.name}</strong>
            {selectedLeader.distance != null ? (
              <span className="ml-1 text-green-500">({selectedLeader.distance} km)</span>
            ) : selectedLeader.approx_distance != null ? (
              <span className="ml-1 text-amber-500">(~{selectedLeader.approx_distance} km)</span>
            ) : null}
          </p>
        )}
      </div>

      <div className="flex gap-2 shrink-0">
        <button
          onClick={onClose}
          className="px-4 py-2 border border-border neu-convex rounded-xl hover:bg-muted transition-colors text-foreground text-sm"
        >
          Cancelar
        </button>
        <button
          onClick={handleAssign}
          disabled={!selectedLeader}
          className="px-4 py-2 bg-purple-600 neu-relief text-white rounded-xl hover:bg-purple-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          Atribuir Lead
        </button>
      </div>
    </div>
  )

  const footerStep2 = (
    <div className="p-4 flex justify-between items-center gap-3">
      <button
        onClick={handleBackToSelection}
        disabled={sending}
        className="px-4 py-2.5 border border-border neu-convex rounded-xl hover:bg-muted transition-colors text-foreground text-sm flex items-center gap-2 disabled:opacity-50"
      >
        <FiArrowLeft className="w-4 h-4" />
        Voltar
      </button>
      <button
        onClick={handleConfirmAndSend}
        disabled={sending || isInvalidContactName(clientData?.name) || (selectedLeader?.has_valid_phone === false && sendTarget !== 'lead_only')}
        title={
          isInvalidContactName(clientData?.name) ? 'Corrija o nome do lead antes de enviar'
          : (selectedLeader?.has_valid_phone === false && sendTarget !== 'lead_only')
            ? 'A líder está sem telefone válido — atualize o cadastro, escolha outra líder ou troque para "Só Lead"'
            : undefined
        }
        className="px-6 py-2.5 bg-green-600 neu-relief text-white rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-semibold flex items-center gap-2"
      >
        {sending ? (
          <>
            <FiLoader className="w-4 h-4 animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            <FiSend className="w-4 h-4" />
            Confirmar e Enviar
          </>
        )}
      </button>
    </div>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      icon={showConfirmation
        ? <FiSend className="w-4 h-4 text-green-500" />
        : <FiUser className="w-4 h-4 text-purple-500" />}
      title={showConfirmation ? 'Confirmar e Enviar' : 'Atribuir Lead para Líder'}
      subtitle={showConfirmation
        ? 'Revise as mensagens antes de enviar'
        : (contactName || contactPhone)
          ? <>Cliente: <span className="font-medium text-foreground">{displayName}</span>{contactPhone ? <span className="ml-1 opacity-70">({contactPhone})</span> : null}</>
          : undefined}
      footer={showConfirmation ? footerStep2 : footerStep1}
    >
        {!showConfirmation ? (
          <>
            {/* ====== STEP 1: Seleção de Líder ====== */}

            {/* Search */}
            <div className="p-4 border-b border-border space-y-3">
              {/* Busca por nome/código */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar por nome, código ou grupo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-input-bg border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>

              {/* Busca por CEP */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <FiMapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="CEP do cliente (para encontrar líder mais próxima)"
                    value={searchCEP}
                    onChange={(e) => setSearchCEP(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    onKeyDown={(e) => { if (e.key === 'Enter' && searchCEP.length === 8) handleCEPSearch() }}
                    className="w-full pl-10 pr-4 py-2.5 bg-input-bg border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    maxLength={8}
                  />
                </div>
                <button
                  onClick={handleCEPSearch}
                  disabled={searchCEP.length < 8 || calculatingDistance}
                  className="px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors shadow-sm"
                >
                  {calculatingDistance ? (
                    <>
                      <FiLoader className="animate-spin" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <FiMapPin />
                      Buscar Próximas
                    </>
                  )}
                </button>
              </div>

              {/* Endereço completo do cliente (geocodificado) — é a referência
                  contra a qual a distância de cada líder é medida. Destacado pra
                  o operador comparar com o endereço mostrado em cada card. */}
              {clientLocation && (() => {
                const enderecoCliente = [clientLocation.street, clientLocation.neighborhood, clientLocation.city, clientLocation.state].filter(Boolean).join(', ')
                if (!enderecoCliente) return null
                return (
                  <div className="flex items-start gap-2 rounded-lg border border-purple-400/40 bg-purple-500/5 px-3 py-2">
                    <FiMapPin className="w-4 h-4 mt-0.5 shrink-0 text-purple-500" />
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-purple-500/90">Endereço do cliente</p>
                      <p className="text-sm text-foreground leading-snug">{enderecoCliente}</p>
                      {clientLocation.geocodedBy && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">geocodificado via {clientLocation.geocodedBy}</p>
                      )}
                      {clientLocation.distanceReliable === false && (
                        <p className="text-[11px] font-medium text-amber-500 mt-1 leading-snug">
                          ⚠️ CEP só resolveu no centro da cidade — as distâncias e a RECOMENDADA são aproximadas. Confira o endereço de cada líder antes de atribuir.
                        </p>
                      )}
                    </div>
                  </div>
                )
              })()}

              {/* Info / avisos do CEP (aproximação por fallback, erros). Destaque
                  âmbar pra não passar batido quando a distância é aproximada. */}
              {cepInfo && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                  <FiAlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{cepInfo}</span>
                </div>
              )}

              {/* Aviso fora-de-MS (não bloqueia — só alerta o operador). A
                  Distribuição atende só MS (CEP 79xxx); CEP de fora não tem
                  líder local. Decisão do dono 03/06: avisar, não bloquear. */}
              {(() => {
                const cepDigits = String(clientData?.cep || searchCEP || '').replace(/\D/g, '')
                const foraDeMs = cepDigits.length === 8 && !cepDigits.startsWith('79')
                if (!foraDeMs) return null
                return (
                  <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                    <FiAlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      CEP <strong>{cepDigits.replace(/(\d{5})(\d{3})/, '$1-$2')}</strong> é de <strong>fora de MS</strong>.
                      A Distribuição atende só Mato Grosso do Sul — confira se a lead é mesmo de outro estado antes de atribuir uma líder.
                    </span>
                  </div>
                )
              })()}
            </div>

            {/* Leaders List */}
            <div className="flex-1 overflow-y-auto p-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <FiLoader className="w-6 h-6 animate-spin text-purple-500" />
                  <span className="ml-2 text-sm text-muted-foreground">Carregando líderes...</span>
                </div>
              ) : filteredLeaders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FiUsers className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Nenhuma líder encontrada
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredLeaders.map((leader) => {
                    const isBestMatch = leader.best_match
                    const isSelected = selectedLeader?.id === leader.id
                    const assignments = leader.weekly_assignments || 0

                    return (
                      <div
                        key={leader.id}
                        onClick={() => selectLeader(leader)}
                        className={`relative p-3.5 rounded-xl border-2 cursor-pointer transition-all neu-item ${
                          leader.is_active === false
                            ? 'border-red-500/50 bg-red-500/5 opacity-75 hover:opacity-100'
                            : isBestMatch
                            ? 'border-amber-400 bg-amber-400/5 shadow-md shadow-amber-400/15 ring-1 ring-amber-400/30'
                            : isSelected
                              ? 'border-purple-500 bg-purple-500/10 shadow-md shadow-purple-500/10'
                              : 'border-border hover:border-purple-400/50 hover:bg-muted/30'
                        }`}
                      >
                        {/* Badge Recomendada — só pra líderes ativas (operador
                            não pode mandar lead pra inativa por engano, visto:
                            GRAUCE 11030301 sem lançamento desde 2018) */}
                        {isBestMatch && leader.is_active !== false && (
                          <div
                            className="absolute -top-2.5 left-4 flex items-center gap-1 px-2 py-0.5 bg-amber-400 text-amber-950 text-[10px] font-bold rounded-full neu-relief"
                            title="Sugestão do sistema: menor distância ao CEP do lead combinada com menor carga de leads na semana. Você pode escolher outra."
                          >
                            <FiStar className="w-3 h-3" />
                            RECOMENDADA
                          </div>
                        )}
                        {/* Badge INATIVA — alerta vermelho proeminente */}
                        {leader.is_active === false && (
                          <div className="absolute -top-2.5 left-4 flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full neu-relief">
                            <FiX className="w-3 h-3" />
                            INATIVA
                            {leader.inactive_since && (
                              <span className="font-normal opacity-90">
                                {' '}· s/ vendas desde {String(leader.inactive_since).slice(4,6)}/{String(leader.inactive_since).slice(0,4)}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Badge TELEFONE INVÁLIDO — só se a normalização nem com DDD 67
                            assumido conseguiu chegar em E.164 válido. Sinaliza pra equipe
                            atualizar o cadastro (caso real LUANA 99158-0435 já é coberto
                            pelo ddd_assumed). Posicionado à direita pra não conflitar com
                            INATIVA/RECOMENDADA. */}
                        {leader.has_valid_phone === false && (
                          <div
                            className="absolute -top-2.5 right-4 flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full shadow-sm"
                            title="Telefone inválido no cadastro Oracle — atualize com DDD + número antes de atribuir"
                          >
                            <FiPhone className="w-3 h-3" />
                            SEM TELEFONE VÁLIDO
                          </div>
                        )}
                        {/* Badge DDD ASSUMIDO — amarelo discreto, não bloqueia envio mas
                            avisa que o cadastro veio sem DDD e o sistema completou com 67.
                            Equipe deve atualizar pra evitar ambiguidade. */}
                        {leader.has_valid_phone !== false && leader.ddd_assumed && (
                          <div
                            className="absolute -top-2.5 right-4 flex items-center gap-1 px-2 py-0.5 bg-amber-500 text-amber-950 text-[10px] font-bold rounded-full shadow-sm"
                            title="Cadastro sem DDD — sistema assumiu 67 (MS). Atualize o cadastro pra evitar erro caso a líder seja de outro estado."
                          >
                            <FiPhone className="w-3 h-3" />
                            DDD 67 ASSUMIDO
                          </div>
                        )}

                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                leader.is_active === false ? 'bg-red-500/15 text-red-500'
                                  : isBestMatch ? 'bg-amber-400 text-amber-950'
                                  : isSelected ? 'bg-purple-500 text-white'
                                  : 'bg-purple-500/15 text-purple-500'
                              }`}>
                                {isBestMatch && leader.is_active !== false ? <FiStar className="w-4 h-4" /> : <FiUser className="w-4 h-4" />}
                              </div>
                              <h3 className={`font-semibold text-sm ${leader.is_active === false ? 'text-red-500' : 'text-foreground'}`}>
                                {leader.name}
                              </h3>
                              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                #{leader.code}
                              </span>
                            </div>

                            <div className="mt-2 ml-10 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                              {/* Endereço cadastral da líder — col inteira, no topo.
                                  Permite conferir se a distância (km) faz sentido vs.
                                  o endereço do cliente. Se há distância mas falta
                                  endereço, a lat/long é suspeita → aviso âmbar. */}
                              {leader.address ? (
                                <div className="col-span-2 flex items-start gap-1.5 text-muted-foreground">
                                  <FiMapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-purple-400" />
                                  <span className="leading-snug">
                                    {leader.address}
                                    {leader.leader_cep && (
                                      <span className="opacity-70"> · CEP {String(leader.leader_cep).replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2')}</span>
                                    )}
                                  </span>
                                </div>
                              ) : leader.distance != null ? (
                                <div className="col-span-2 flex items-start gap-1.5 text-amber-600 dark:text-amber-400" title="Líder sem endereço cadastrado — a distância vem só da latitude/longitude e não pode ser conferida. Verifique o cadastro antes de confiar no 'mais próximo'.">
                                  <FiAlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                  <span className="leading-snug">Sem endereço cadastrado — distância não verificável</span>
                                </div>
                              ) : null}

                              {leader.group_name && (
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                  <FiUsers className="w-3.5 h-3.5" />
                                  {leader.group_name}
                                </div>
                              )}

                              {leader.phone && (
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                  <FiPhone className="w-3.5 h-3.5" />
                                  {leader.phone}
                                </div>
                              )}

                              {/* Presença de WhatsApp (passivo, dos handoffs reais).
                                  Vermelho = último envio falhou (provável número sem
                                  WhatsApp); verde = já entregou antes. Sem badge =
                                  ainda não recebeu handoff (não verificado). */}
                              {leader.wa_status?.has_whatsapp === false ? (
                                <div
                                  className="flex items-center gap-1.5 text-red-500 font-medium"
                                  title={`Último envio falhou${leader.wa_status.last_error_code ? ' (erro ' + leader.wa_status.last_error_code + ')' : ''} — número provavelmente sem WhatsApp. Confira o cadastro antes de atribuir.`}
                                >
                                  <FiX className="w-3.5 h-3.5" />
                                  sem WhatsApp
                                </div>
                              ) : leader.wa_status?.has_whatsapp === true ? (
                                <div
                                  className="flex items-center gap-1.5 text-green-600"
                                  title="WhatsApp confirmado por entrega de handoff anterior."
                                >
                                  <FiCheck className="w-3.5 h-3.5" />
                                  WhatsApp ✓
                                </div>
                              ) : null}

                              {/* Guardrail de entrega: falhas de notificação nos últimos
                                  14 dias (mesma janela do auto-assign, que excluiria essa
                                  líder automaticamente — aqui só avisa, quem decide é o
                                  operador). Não é "sem WhatsApp": é "a Meta não está
                                  entregando template pra esse número ultimamente". */}
                              {!!leader.recentFails && (
                                <div
                                  className="flex items-center gap-1.5 text-amber-600 font-medium"
                                  title={`${leader.recentFails} notificação${leader.recentFails === 1 ? '' : 'ões'} de handoff não entregue${leader.recentFails === 1 ? '' : 's'} pra essa líder nos últimos 14 dias — a Meta pode estar bloqueando entrega pra esse número. Confirme por outro canal se atribuir.`}
                                >
                                  <FiAlertTriangle className="w-3.5 h-3.5" />
                                  {leader.recentFails} falha{leader.recentFails === 1 ? '' : 's'} de entrega
                                </div>
                              )}

                              {leader.recrutas_count !== undefined && (
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                  <FiUsers className="w-3.5 h-3.5" />
                                  {leader.recrutas_count} consultoras
                                </div>
                              )}

                              {leader.own_sales != null && (
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                  <FiAward className="w-3.5 h-3.5" />
                                  R$ {Number(leader.own_sales).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Métricas: Distância + Atribuições */}
                          <div className="flex items-start gap-2 ml-3 flex-shrink-0">
                            {leader.distance != null ? (
                              <div className="flex flex-col items-center bg-green-500/10 text-green-500 px-2.5 py-1.5 rounded-lg neu-tag"
                                title="Distância em linha reta entre o endereço da líder e o CEP do lead">
                                <FiMapPin className="w-4 h-4 mb-0.5" />
                                <span className="text-xs font-bold">
                                  {leader.distance} km
                                </span>
                              </div>
                            ) : leader.approx_distance != null ? (
                              /* [2026-07-27] Antes esta líder não mostrava distância NENHUMA e
                                 ia pro fim da lista: a coordenada dela é o centro da cidade
                                 (geo_source='nominatim_city'), o que o backend trata como
                                 não-confiável. Só que "não-confiável" virava "invisível", e em
                                 cidade com uma líder só o lead ficava sem ninguém por perto —
                                 era a queixa do CEP. Agora aparece com a distância aproximada
                                 e o aviso do que ela significa. */
                              <div className="flex flex-col items-center bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2.5 py-1.5 rounded-lg neu-tag"
                                title="Distância APROXIMADA: o cadastro desta líder só tem a coordenada do centro da cidade, não do endereço dela. Serve pra saber que é da região, não pra comparar quarteirões.">
                                <FiMapPin className="w-4 h-4 mb-0.5" />
                                <span className="text-xs font-bold">
                                  ~{leader.approx_distance} km
                                </span>
                              </div>
                            ) : null}

                            <div className={`flex flex-col items-center px-2.5 py-1.5 rounded-lg neu-tag ${
                              assignments === 0
                                ? 'bg-emerald-500/10 text-emerald-500'
                                : assignments <= 2
                                  ? 'bg-yellow-500/10 text-yellow-500'
                                  : 'bg-red-500/10 text-red-500'
                            }`}
                              title={`${assignments} lead${assignments === 1 ? '' : 's'} atribuído${assignments === 1 ? '' : 's'} a esta líder nos últimos 7 dias`}>
                              <FiInbox className={`w-4 h-4 mb-0.5 ${
                                assignments === 0 ? 'text-emerald-500' : assignments <= 2 ? 'text-yellow-500' : 'text-red-500'
                              }`} />
                              <span className={`text-xs font-bold ${
                                assignments === 0 ? 'text-emerald-500' : assignments <= 2 ? 'text-yellow-500' : 'text-red-500'
                              }`}>
                                {assignments}x
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

          </>
        ) : (
          <>
            {/* ====== STEP 2: Confirmação de Mensagens ====== */}

            <div className="p-4 border-b border-border">
              {/* Resumo visual: Líder ← → Cliente */}
              <div className="mt-3 flex items-center gap-3 p-3 bg-muted/40 rounded-xl">
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                    <FiUser className="w-4 h-4 text-purple-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Líder</p>
                    <p className="text-sm font-medium text-foreground truncate">{selectedLeader?.name}</p>
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1 text-muted-foreground">
                  <FiSend className="w-3 h-3" />
                </div>
                <div className="flex-1 flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-green-500/15 flex items-center justify-center flex-shrink-0">
                    <FiPhone className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Cliente</p>
                    <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Message editing area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">

              {/* Toggle de destinatário: caminho normal é mandar pros dois.
                  "Só líder" / "Só lead" servem pra retry quando um lado já foi
                  notificado com sucesso na tentativa anterior — evita queimar
                  template Meta repetido ou mandar "Pronto" duplicado pra lead. */}
              <div className="flex items-center gap-2 p-3 bg-muted/30 border border-border rounded-lg">
                <span className="text-xs font-medium text-muted-foreground shrink-0">Enviar pra:</span>
                <div className="flex gap-1.5 flex-1">
                  {([
                    { key: 'both' as const, label: 'Lead + Líder', desc: 'Envio padrão — usar quando é a primeira atribuição.' },
                    { key: 'leader_only' as const, label: 'Só Líder', desc: 'Retry pro template da líder. Lead NÃO recebe "Pronto" — útil quando ela já foi avisada.' },
                    { key: 'lead_only' as const, label: 'Só Lead', desc: 'Reenvia só o "Pronto, X! A líder vai te chamar". Líder NÃO recebe template — útil quando ela já recebeu.' }
                  ]).map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSendTarget(opt.key)}
                      disabled={sending}
                      title={opt.desc}
                      className={`flex-1 px-2.5 py-1.5 text-xs rounded-md border transition-colors ${
                        sendTarget === opt.key
                          ? 'bg-purple-500/15 border-purple-500/50 text-purple-700 dark:text-purple-300 font-medium'
                          : 'bg-transparent border-border text-muted-foreground hover:bg-muted/50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Error message — multiline (sumário + lista de erros do backend) */}
              {sendError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-start gap-2 whitespace-pre-line">
                  <FiX className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">{sendError}</div>
                </div>
              )}

              {/* Warning se líder não tem telefone válido (sem cadastro OU
                  cadastro inválido — caso da raiz LUANA antes do DDD assumido).
                  has_valid_phone === false cobre os dois cenários hoje. */}
              {selectedLeader?.has_valid_phone === false && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
                  <FiPhone className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium mb-1">Líder sem telefone válido</div>
                    <div className="text-xs opacity-90">
                      Cadastro Oracle: <code className="bg-red-500/10 px-1 rounded">{selectedLeader?.phone || '(vazio)'}</code>. O envio à líder está bloqueado — atualize o cadastro com DDD + número, escolha outra líder, ou troque para "Só Lead" para avisar apenas o cliente.
                    </div>
                  </div>
                </div>
              )}

              {/* Aviso quando DDD 67 foi assumido — não bloqueia, mas sinaliza
                  pra equipe atualizar o cadastro. */}
              {selectedLeader?.has_valid_phone !== false && selectedLeader?.ddd_assumed && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/40 rounded-lg text-amber-800 dark:text-amber-200 text-sm flex items-start gap-2">
                  <FiPhone className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold mb-1">DDD 67 (MS) assumido</div>
                    <div className="text-xs opacity-95">
                      O cadastro veio sem DDD ({selectedLeader?.phone}). Sistema completou com 67 pra enviar. Se a líder não for de MS, o envio vai falhar — atualize o cadastro.
                    </div>
                  </div>
                </div>
              )}

              {/* AVISO DE NOME DIVERGENTE — [2026-07-27]
                  O que a pessoa DIGITOU no chat difere do nome que veio do perfil
                  do WhatsApp / cadastro. Em linha de familia sao pessoas diferentes:
                  a Natalia digitou o nome dela e a lider Margarida recebeu "CLECY
                  ROSA DE SOUZA" (dona canonica da linha). Nao troca nada sozinho —
                  mostra os dois e deixa a operadora escolher. */}
              {clientData?.nameFromChat
                && clientData.nameFromChat.toLowerCase() !== (clientData.name || '').toLowerCase()
                && !isInvalidContactName(clientData?.name) && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/40 rounded-lg text-amber-800 dark:text-amber-200 text-sm flex items-start gap-2">
                  <FiUser className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold mb-1">Nome divergente — confira antes de enviar</div>
                    <div className="text-xs opacity-95 mb-2">
                      No chat a pessoa se identificou como <strong>{clientData.nameFromChat}</strong>, mas o
                      nome que vai ser enviado é <strong>{clientData.name}</strong> (perfil do WhatsApp/cadastro
                      da linha). Se o número é de família, quem está falando pode não ser a dona do cadastro.
                    </div>
                    <button
                      type="button"
                      onClick={() => applyLeadName(clientData.nameFromChat as string)}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium transition-colors"
                    >
                      Usar &quot;{clientData.nameFromChat}&quot;
                    </button>
                  </div>
                </div>
              )}

              {/* Validação do nome do lead — bloqueia envio se inválido.
                  Antes desse guard, "Pronto, *!" vazava pra produção (caso Alessandra 26/05 8:35).
                  [2026-07-27] Também abre quando o backend barrou por número
                  compartilhado: sem isto a operadora recebia o 409 sem ter onde
                  digitar o nome certo — beco sem saída. */}
              {(isInvalidContactName(clientData?.name) || sharedNumberBlocked) && (
                <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-700 dark:text-orange-300 text-sm flex items-start gap-2">
                  <FiUser className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium mb-1">
                      {sharedNumberBlocked && !isInvalidContactName(clientData?.name)
                        ? 'Confirme o nome da lead'
                        : 'Nome do lead inválido'}
                    </div>
                    <div className="text-xs opacity-90 mb-2">
                      {sharedNumberBlocked && !isInvalidContactName(clientData?.name) ? (
                        <>
                          O nome <strong>{clientData?.name}</strong> veio do cadastro do número
                          {sharedNumberOwners.length > 0 && <> ({sharedNumberOwners.join(', ')})</>}
                          , não do que a pessoa disse no chat. Em linha de família quem está
                          falando costuma ser outra pessoa. Digite o nome de quem está falando
                          e clique em Confirmar de novo.
                        </>
                      ) : (
                        <>O nome extraído ("{clientData?.name || '(vazio)'}") é placeholder ou inválido. Digite o nome completo antes de atribuir a líder.</>
                      )}
                    </div>
                    <input
                      type="text"
                      value={clientData?.name && clientData.name !== 'Não informado' ? clientData.name : ''}
                      onChange={(e) => applyLeadName(e.target.value)}
                      placeholder="Nome completo do lead"
                      className="w-full px-3 py-2 bg-background border border-orange-500/40 rounded text-foreground text-sm focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>
              )}

              {/* Message to Leader — WhatsApp style card */}
              <div className={`rounded-xl border overflow-hidden transition-opacity ${
                sendTarget === 'lead_only' ? 'border-border opacity-40' : 'border-purple-500/20'
              }`}>
                <div className={`px-4 py-2.5 flex items-center justify-between ${
                  sendTarget === 'lead_only' ? 'bg-muted/30' : 'bg-purple-500/10'
                }`}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                      <FiUser className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm font-medium text-purple-700 dark:text-purple-300">{selectedLeader?.name}</span>
                    {sendTarget === 'lead_only' && (
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        Não será enviada
                      </span>
                    )}
                  </div>
                  {selectedLeader?.phone && (
                    <span className="text-xs text-muted-foreground">{selectedLeader.phone}</span>
                  )}
                </div>
                {/* [2026-07-27] A prévia NÃO é o que sai. O backend remonta a mensagem
                    do zero a partir do template aprovado da Meta (encaminhar_lead_lider,
                    4 parâmetros) — CPF e endereço completo ficam de fora, e a saudação e
                    o gênero saem diferentes. Editar o texto aqui também não muda nada no
                    envio. Em vez de esconder os campos (a atendente usa CPF e endereço
                    pra conferir com a lead), deixamos claro o que atravessa. */}
                <div className="px-4 pt-2.5 text-[11px] text-muted-foreground flex items-start gap-1.5">
                  <FiAlertTriangle className="w-3.5 h-3.5 mt-px shrink-0 text-amber-500" />
                  <span>
                    A líder recebe apenas <strong className="text-foreground">Nome</strong>,{' '}
                    <strong className="text-foreground">Telefone</strong> e{' '}
                    <strong className="text-foreground">Bairro/Cidade</strong>. CPF e endereço
                    completo aparecem aqui só pra sua conferência — não são enviados. O texto
                    abaixo é uma prévia: o envio usa o template aprovado da Meta.
                  </span>
                </div>
                <textarea
                  value={leaderMessage}
                  onChange={(e) => setLeaderMessage(e.target.value)}
                  rows={9}
                  disabled={sending || sendTarget === 'lead_only'}
                  className="w-full p-4 bg-transparent border-0 text-foreground text-sm resize-y focus:outline-none focus:ring-0 disabled:opacity-50 leading-relaxed"
                  style={{ minHeight: '180px' }}
                />
              </div>

              {/* Message to Client — WhatsApp style card */}
              <div className={`rounded-xl border overflow-hidden transition-opacity ${
                sendTarget === 'leader_only' ? 'border-border opacity-40' : 'border-green-500/20'
              }`}>
                <div className={`px-4 py-2.5 flex items-center justify-between ${
                  sendTarget === 'leader_only' ? 'bg-muted/30' : 'bg-green-500/10'
                }`}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <FiPhone className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm font-medium text-green-700 dark:text-green-300">{displayName}</span>
                    {sendTarget === 'leader_only' && (
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        Não será enviada
                      </span>
                    )}
                  </div>
                  {contactPhone && (
                    <span className="text-xs text-muted-foreground">{contactPhone}</span>
                  )}
                </div>
                <textarea
                  value={clientMessage}
                  onChange={(e) => setClientMessage(e.target.value)}
                  rows={3}
                  disabled={sending || sendTarget === 'leader_only'}
                  className="w-full p-4 bg-transparent border-0 text-foreground text-sm resize-y focus:outline-none focus:ring-0 disabled:opacity-50 leading-relaxed"
                  style={{ minHeight: '80px' }}
                />
              </div>
            </div>

          </>
        )}
    </SidePanel>
  )
}
